

import React from 'react';
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

// ============================================================
// ERROR BOUNDARY
// ============================================================

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,

      currentScreen:
        props.screenName ||
        null,

      currentRouteParams:
        props.params ||
        null,

      currentPath:
        props.location?.pathname ||
        window.location.pathname,

      errorStack: [],
      componentHierarchy: [],
      navigationHistory: [],

      showDebugInfo: false,

      errorId: null,
    };

    this.navigationTimer = null;
  }

  // ==========================================================
  // CATCH RENDERING ERRORS
  // ==========================================================

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error,
      errorId: `ERR_${Date.now()}_${Math.random()
        .toString(36)
        .substring(2, 11)}`,
    };
  }

  // ==========================================================
  // COMPONENT DID CATCH
  // ==========================================================

  componentDidCatch(error, errorInfo) {
    const componentInfo =
      this.extractComponentInfo(
        error,
        errorInfo
      );

    const screenInfo =
      this.extractScreenInfo(
        errorInfo
      );

    const componentHierarchy =
      this.buildComponentHierarchy(
        errorInfo
      );

    const errorStack =
      this.getErrorStack(
        errorInfo
      );

    const currentPath =
      this.props.location?.pathname ||
      window.location.pathname;

    this.setState({
      error,
      errorInfo,
      errorStack,
      componentHierarchy,

      currentScreen:
        screenInfo.screenName ||
        this.props.screenName ||
        currentPath,

      currentRouteParams:
        screenInfo.routeParams ||
        this.props.params ||
        null,

      currentPath,
    });

    // ========================================================
    // ERROR LOGGING
    // ========================================================

    console.error(
      '════════════════════════════════════════════════'
    );

    console.error(
      '🔴 ERROR BOUNDARY CAUGHT AN ERROR'
    );

    console.error(
      '════════════════════════════════════════════════'
    );

    console.error(
      '📱 Screen:',
      screenInfo.screenName ||
        this.props.screenName ||
        'Unknown'
    );

    console.error(
      '🧩 Component:',
      componentInfo.componentName ||
        'Unknown'
    );

    console.error(
      '📍 Line:',
      componentInfo.lineNumber ||
        'Unknown'
    );

    console.error(
      '🌐 Path:',
      currentPath
    );

    console.error(
      '💬 Message:',
      error?.message ||
        'No message'
    );

    console.error(
      '🔄 Route Params:',
      screenInfo.routeParams ||
        this.props.params ||
        null
    );

    console.error(
      '════════════════════════════════════════════════'
    );

    // ========================================================
    // SAVE ERROR LOG
    // ========================================================

    this.logError(
      error,
      errorInfo,
      screenInfo,
      componentInfo
    );
  }

  // ==========================================================
  // COMPONENT DID MOUNT
  // ==========================================================

  componentDidMount() {
    this.updateRouteInformation();

    // Listen for browser navigation.
    this.navigationTimer =
      window.setInterval(() => {
        this.updateRouteInformation();
      }, 250);
  }

  // ==========================================================
  // COMPONENT WILL UNMOUNT
  // ==========================================================

  componentWillUnmount() {
    if (this.navigationTimer) {
      window.clearInterval(
        this.navigationTimer
      );

      this.navigationTimer = null;
    }
  }

  // ==========================================================
  // UPDATE ROUTE INFORMATION
  // ==========================================================

  updateRouteInformation = () => {
    if (
      this.state.hasError
    ) {
      return;
    }

    const pathname =
      window.location.pathname;

    const search =
      window.location.search;

    const hash =
      window.location.hash;

    if (
      pathname !==
        this.state.currentPath
    ) {
      this.setState({
        currentPath:
          pathname,

        currentScreen:
          this.props.screenName ||
          pathname,

        currentRouteParams:
          this.props.params ||
          null,
      });

      this.updateNavigationHistory(
        pathname
      );
    }

    // Keep query/hash information available.
    if (
      this.state.currentPath ===
      pathname
    ) {
      this.setState(
        prevState => ({
          currentPath:
            pathname,

          currentScreen:
            prevState.currentScreen ||
            this.props.screenName ||
            pathname,
        })
      );
    }

    return {
      pathname,
      search,
      hash,
    };
  };

  // ==========================================================
  // NAVIGATION HISTORY
  // ==========================================================

  updateNavigationHistory = (
    pathname = window.location.pathname
  ) => {
    const timestamp =
      new Date().toISOString();

    this.setState(
      prevState => {
        const previous =
          prevState.navigationHistory;

        const lastEntry =
          previous[
            previous.length - 1
          ];

        // Do not add duplicate consecutive
        // routes.
        if (
          lastEntry &&
          lastEntry.screen ===
            pathname
        ) {
          return null;
        }

        return {
          navigationHistory: [
            ...previous.slice(-9),

            {
              screen: pathname,
              timestamp,
              params:
                this.props.params ||
                null,
            },
          ],
        };
      }
    );
  };

  // ==========================================================
  // CURRENT ROUTE PARAMS
  // ==========================================================

  getCurrentRouteParams = () => {
    return (
      this.props.params ||
      this.state.currentRouteParams ||
      null
    );
  };

  // ==========================================================
  // ACTIVE ROUTE
  // ==========================================================

  getActiveRoute = () => {
    return {
      pathname:
        window.location.pathname,

      search:
        window.location.search,

      hash:
        window.location.hash,

      params:
        this.getCurrentRouteParams(),
    };
  };

  // ==========================================================
  // ACTIVE ROUTE NAME
  // ==========================================================

  getActiveRouteName = () => {
    return (
      this.props.screenName ||
      window.location.pathname
    );
  };

  // ==========================================================
  // EXTRACT COMPONENT INFORMATION
  // ==========================================================

  extractComponentInfo = (
    error,
    errorInfo
  ) => {
    const componentStack =
      errorInfo?.componentStack ||
      '';

    const lines =
      componentStack.split('\n');

    // React component stack
    for (
      const line of lines
    ) {
      const componentMatch =
        line.match(
          /(?:in|at)\s+([A-Za-z0-9_$.-]+)/
        );

      if (
        componentMatch &&
        componentMatch[1]
      ) {
        const componentName =
          componentMatch[1];

        if (
          componentName !==
            'ErrorBoundary'
        ) {
          return {
            componentName,
            fullStackLine:
              line.trim(),

            lineNumber:
              this.extractLineNumber(
                line
              ),
          };
        }
      }
    }

    // JavaScript error stack
    const errorStack =
      error?.stack || '';

    const errorLines =
      errorStack.split('\n');

    for (
      const line of errorLines
    ) {
      const match =
        line.match(
          /at\s+([A-Za-z0-9_$.-]+)/
        );

      if (
        match &&
        match[1] &&
        !match[1].includes(
          'ErrorBoundary'
        )
      ) {
        return {
          componentName:
            match[1],

          fullStackLine:
            line.trim(),

          lineNumber:
            this.extractLineNumber(
              line
            ),
        };
      }
    }

    return {
      componentName:
        'Unknown Component',

      fullStackLine:
        'Unable to determine',

      lineNumber:
        null,
    };
  };

  // ==========================================================
  // EXTRACT LINE NUMBER
  // ==========================================================

  extractLineNumber = (
    stackLine
  ) => {
    if (!stackLine) {
      return null;
    }

    // Chrome / Edge / Firefox
    const patterns = [
      /:(\d+):\d+\)?$/,
      /:(\d+):\d+\)/,
      /:(\d+):\d+/,
    ];

    for (
      const pattern of patterns
    ) {
      const match =
        stackLine.match(
          pattern
        );

      if (match) {
        return parseInt(
          match[1],
          10
        );
      }
    }

    return null;
  };

  // ==========================================================
  // EXTRACT SCREEN INFORMATION
  // ==========================================================

  extractScreenInfo = (
    errorInfo
  ) => {
    const componentStack =
      errorInfo?.componentStack ||
      '';

    const lines =
      componentStack.split('\n');

    const screenPatterns = [
      /in\s+([A-Za-z0-9_$.-]*Screen)/i,

      /in\s+(DiscoverScreen)/i,
      /in\s+(ProfileScreen)/i,
      /in\s+(ChatScreen)/i,
      /in\s+(MatchScreen)/i,
      /in\s+(PinScreen)/i,
      /in\s+(ViewProfileScreen)/i,
      /in\s+(EditProfileScreen)/i,
      /in\s+(BlockScreen)/i,
      /in\s+(LoginScreen)/i,
      /in\s+(PrivacyPolicyScreen)/i,
      /in\s+(RegisterScreen)/i,
      /in\s+(ReportScreen)/i,
      /in\s+(SafetyTipsScreen)/i,
      /in\s+(SettingScreen)/i,
      /in\s+(SupportScreen)/i,
      /in\s+(TermsOfServiceScreen)/i,
      /in\s+(PlanScreen)/i,
      /in\s+(LikedScreen)/i,
      /in\s+(HelpCenterScreen)/i,
      /in\s+(ChatListScreen)/i,
      /in\s+(CompleteProfileScreen)/i,

      // Web application names
      /in\s+(AdminLoginPage)/i,
      /in\s+(AdminDashboard)/i,
      /in\s+(AdminAppContent)/i,
      /in\s+(AppContent)/i,
      /in\s+(ProviderDashboard)/i,
      /in\s+(BusinessVerificationPage)/i,
    ];

    for (
      const line of lines
    ) {
      for (
        const pattern of screenPatterns
      ) {
        const match =
          line.match(pattern);

        if (
          match &&
          match[1]
        ) {
          return {
            screenName:
              match[1],

            routeParams:
              this.getCurrentRouteParams(),
          };
        }
      }
    }

    return {
      screenName:
        this.props.screenName ||
        this.state?.currentScreen ||
        window.location.pathname ||
        'Unknown Screen',

      routeParams:
        this.getCurrentRouteParams(),
    };
  };

  // ==========================================================
  // BUILD COMPONENT HIERARCHY
  // ==========================================================

  buildComponentHierarchy = (
    errorInfo
  ) => {
    const componentStack =
      errorInfo?.componentStack ||
      '';

    const lines =
      componentStack.split('\n');

    const hierarchy = [];

    for (
      const line of lines
    ) {
      const match =
        line.match(
          /(?:in|at)\s+([A-Za-z0-9_$.-]+)/
        );

      if (
        match &&
        match[1]
      ) {
        hierarchy.push({
          component:
            match[1],

          fullLine:
            line.trim(),
        });
      }
    }

    return hierarchy;
  };

  // ==========================================================
  // GET ERROR STACK
  // ==========================================================

  getErrorStack = (
    errorInfo
  ) => {
    if (
      !errorInfo ||
      !errorInfo.componentStack
    ) {
      return [];
    }

    return errorInfo.componentStack
      .split('\n')
      .filter(
        line =>
          line.trim()
      )
      .map(line => ({
        raw:
          line.trim(),

        component:
          this.extractComponentFromStackLine(
            line
          ),

        lineNumber:
          this.extractLineNumber(
            line
          ),
      }));
  };

  // ==========================================================
  // EXTRACT COMPONENT FROM STACK LINE
  // ==========================================================

  extractComponentFromStackLine = (
    line
  ) => {
    const match =
      line.match(
        /(?:in|at)\s+([A-Za-z0-9_$.-]+)/
      );

    return match
      ? match[1]
      : 'Unknown';
  };

  // ==========================================================
  // LOG ERROR
  // ==========================================================

  logError = async (
    error,
    errorInfo,
    screenInfo,
    componentInfo
  ) => {
    try {
      const errorLog = {
        timestamp:
          new Date().toISOString(),

        errorId:
          this.state.errorId ||
          `ERR_${Date.now()}`,

        // ====================================================
        // SCREEN
        // ====================================================

        screen: {
          name:
            screenInfo.screenName,

          path:
            window.location.pathname,

          search:
            window.location.search,

          hash:
            window.location.hash,

          params:
            screenInfo.routeParams,

          navigationHistory:
            this.state.navigationHistory,
        },

        // ====================================================
        // COMPONENT
        // ====================================================

        component: {
          name:
            componentInfo.componentName,

          hierarchy:
            this.state.componentHierarchy,

          lineNumber:
            componentInfo.lineNumber,

          stackLine:
            componentInfo.fullStackLine,
        },

        // ====================================================
        // ERROR
        // ====================================================

        error: {
          message:
            error?.message ||
            'Unknown error',

          name:
            error?.name ||
            'Error',

          stack:
            error?.stack ||
            null,

          type:
            this.getErrorType(
              error?.message
            ),
        },

        // ====================================================
        // ERROR INFO
        // ====================================================

        errorInfo: {
          componentStack:
            errorInfo?.componentStack ||
            null,
        },

        // ====================================================
        // ENVIRONMENT
        // ====================================================

        platform:
          'web',

        browser:
          navigator.userAgent,

        url:
          window.location.href,

        version:
          this.props.appVersion ||
          '1.0.0',

        // ====================================================
        // USER CONTEXT
        // ====================================================

        userContext:
          this.props.userContext ||
          null,
      };

      // ======================================================
      // STORE LOCALLY
      // ======================================================

      try {
        const existingLogs =
          JSON.parse(
            localStorage.getItem(
              'application_error_logs'
            ) || '[]'
          );

        existingLogs.push(
          errorLog
        );

        // Keep last 20 errors.
        localStorage.setItem(
          'application_error_logs',
          JSON.stringify(
            existingLogs.slice(-20)
          )
        );
      } catch (
        storageError
      ) {
        console.error(
          'Failed to save error to localStorage:',
          storageError
        );
      }

      // ======================================================
      // CONSOLE
      // ======================================================

      console.error(
        '📝 Error logged:',
        errorLog
      );

      console.error(
        '🆔 Error ID:',
        errorLog.errorId
      );

      return errorLog;
    } catch (
      logError
    ) {
      console.error(
        'Failed to log error:',
        logError
      );

      return null;
    }
  };

  // ==========================================================
  // ERROR TYPE
  // ==========================================================

  getErrorType = (
    errorMessage
  ) => {
    if (!errorMessage) {
      return 'Unknown';
    }

    if (
      errorMessage.includes(
        'Text strings must be rendered'
      )
    ) {
      return 'TextRenderingError';
    }

    if (
      errorMessage.includes(
        'undefined is not an object'
      )
    ) {
      return 'UndefinedObjectError';
    }

    if (
      errorMessage.includes(
        'Network request failed'
      )
    ) {
      return 'NetworkError';
    }

    if (
      errorMessage.toLowerCase().includes(
        'network'
      )
    ) {
      return 'NetworkError';
    }

    if (
      errorMessage
        .toLowerCase()
        .includes('component')
    ) {
      return 'ComponentError';
    }

    if (
      errorMessage
        .toLowerCase()
        .includes('cannot read')
    ) {
      return 'UndefinedObjectError';
    }

    return 'RuntimeError';
  };

  // ==========================================================
  // SHARE / DOWNLOAD ERROR LOG
  // ==========================================================

  shareErrorLog = async () => {
    try {
      const errorLog = {
        errorId:
          this.state.errorId ||
          `ERR_${Date.now()}`,

        timestamp:
          new Date().toISOString(),

        screen:
          this.state.currentScreen,

        path:
          this.state.currentPath,

        screenParams:
          this.state.currentRouteParams,

        componentHierarchy:
          this.state.componentHierarchy,

        errorMessage:
          this.state.error?.message,

        errorName:
          this.state.error?.name,

        errorStack:
          this.state.error?.stack,

        componentStack:
          this.state.errorInfo
            ?.componentStack,

        navigationHistory:
          this.state.navigationHistory,

        platform:
          'web',

        url:
          window.location.href,

        browser:
          navigator.userAgent,
      };

      const errorLogString =
        JSON.stringify(
          errorLog,
          null,
          2
        );

      const fileName =
        `error_report_${errorLog.errorId}.json`;

      // ======================================================
      // BROWSER DOWNLOAD
      // ======================================================

      const blob =
        new Blob(
          [errorLogString],
          {
            type:
              'application/json;charset=utf-8',
          }
        );

      const url =
        URL.createObjectURL(
          blob
        );

      const anchor =
        document.createElement(
          'a'
        );

      anchor.href = url;
      anchor.download = fileName;

      document.body.appendChild(
        anchor
      );

      anchor.click();

      document.body.removeChild(
        anchor
      );

      URL.revokeObjectURL(
        url
      );

      this.showBrowserMessage(
        'Error report downloaded successfully.'
      );
    } catch (
      shareError
    ) {
      console.error(
        'Failed to download error report:',
        shareError
      );

      this.showBrowserMessage(
        'Failed to create error report.'
      );
    }
  };

  // ==========================================================
  // BROWSER MESSAGE
  // ==========================================================

  showBrowserMessage = (
    message
  ) => {
    // Use a small temporary notification
    // instead of React Native Alert.
    const notification =
      document.createElement(
        'div'
      );

    notification.textContent =
      message;

    notification.className =
      'error-boundary-notification';

    document.body.appendChild(
      notification
    );

    window.setTimeout(() => {
      notification.remove();
    }, 3000);
  };

  // ==========================================================
  // COPY ERROR DETAILS
  // ==========================================================

  copyErrorDetails = async () => {
    try {
      const details = [
        `Error ID: ${
          this.state.errorId ||
          'N/A'
        }`,

        `Screen: ${
          this.state.currentScreen ||
          'Unknown'
        }`,

        `Path: ${
          this.state.currentPath ||
          window.location.pathname
        }`,

        `Component: ${
          this.state
            .componentHierarchy?.[0]
            ?.component ||
          'Unknown'
        }`,

        `Error: ${
          this.state.error?.message ||
          'Unknown error'
        }`,
      ].join('\n');

      if (
        navigator.clipboard &&
        window.isSecureContext
      ) {
        await navigator.clipboard.writeText(
          details
        );
      } else {
        const textarea =
          document.createElement(
            'textarea'
          );

        textarea.value =
          details;

        textarea.style.position =
          'fixed';

        textarea.style.opacity =
          '0';

        document.body.appendChild(
          textarea
        );

        textarea.focus();
        textarea.select();

        document.execCommand(
          'copy'
        );

        textarea.remove();
      }

      this.showBrowserMessage(
        'Error details copied to clipboard.'
      );
    } catch (
      copyError
    ) {
      console.error(
        'Failed to copy error details:',
        copyError
      );

      this.showBrowserMessage(
        'Unable to copy error details.'
      );
    }
  };

  // ==========================================================
  // RETRY
  // ==========================================================

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorStack: [],
      componentHierarchy: [],
      errorId: null,
      showDebugInfo: false,
    });

    if (
      typeof this.props.onRetry ===
      'function'
    ) {
      this.props.onRetry();
    }
  };

  // ==========================================================
  // RESET / GO HOME
  // ==========================================================

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorStack: [],
      componentHierarchy: [],
      currentScreen: null,
      currentRouteParams: null,
      errorId: null,
      showDebugInfo: false,
    });

    // ========================================================
    // React Router navigation
    // ========================================================

    if (
      typeof this.props.navigation ===
      'function'
    ) {
      this.props.navigation(
        this.props.homePath ||
          '/'
      );

      return;
    }

    // ========================================================
    // Fallback
    // ========================================================

    window.location.href =
      this.props.homePath ||
      '/';
  };

  // ==========================================================
  // RENDER ERROR DETAILS
  // ==========================================================

  renderErrorDetails = () => {
    const {
      error,
      errorStack,
      componentHierarchy,
      currentScreen,
      currentRouteParams,
      showDebugInfo,
      navigationHistory,
      currentPath,
    } = this.state;

    return (
      <div className="error-details">

        {/* ==================================================
            TITLE
        ================================================== */}

        <h2 className="error-detail-title">
          🔍 Error Details
        </h2>

        {/* ==================================================
            SCREEN
        ================================================== */}

        <section className="error-detail-section">
          <div className="error-detail-label">
            📱 Screen
          </div>

          <div className="error-detail-value">
            <div className="error-detail-text">
              {currentScreen ||
                'Unknown'}
            </div>

            <div className="error-detail-subtext">
              Path:{' '}
              {currentPath ||
                '/'}
            </div>

            {currentRouteParams &&
              Object.keys(
                currentRouteParams
              ).length > 0 && (
                <pre className="error-detail-subtext">
                  Params:{' '}
                  {JSON.stringify(
                    currentRouteParams,
                    null,
                    2
                  )}
                </pre>
              )}
          </div>
        </section>

        {/* ==================================================
            COMPONENT HIERARCHY
        ================================================== */}

        <section className="error-detail-section">
          <div className="error-detail-label">
            🧩 Component Hierarchy
          </div>

          <div className="error-detail-value">
            {componentHierarchy.length >
            0 ? (
              componentHierarchy.map(
                (
                  comp,
                  index
                ) => (
                  <div
                    key={`${comp.component}-${index}`}
                    className="error-stack-line"
                  >
                    {index + 1}.{' '}
                    {comp.component}
                  </div>
                )
              )
            ) : (
              <div className="error-detail-subtext">
                Unable to determine
                component hierarchy.
              </div>
            )}
          </div>
        </section>

        {/* ==================================================
            ERROR MESSAGE
        ================================================== */}

        <section className="error-detail-section">
          <div className="error-detail-label">
            💬 Error Message
          </div>

          <div className="error-detail-value error-message">
            <div className="error-detail-text">
              {error?.message ||
                'No message'}
            </div>
          </div>
        </section>

        {/* ==================================================
            ERROR TYPE
        ================================================== */}

        <section className="error-detail-section">
          <div className="error-detail-label">
            ⚠️ Error Type
          </div>

          <div className="error-detail-value">
            <div className="error-detail-text">
              {this.getErrorType(
                error?.message
              )}
            </div>
          </div>
        </section>

        {/* ==================================================
            NAVIGATION HISTORY
        ================================================== */}

        {navigationHistory.length >
          0 && (
          <section className="error-detail-section">
            <div className="error-detail-label">
              🔄 Navigation History
            </div>

            <div className="error-detail-value">
              {navigationHistory.map(
                (
                  nav,
                  index
                ) => (
                  <div
                    key={`${nav.screen}-${index}`}
                    className="error-stack-line"
                  >
                    {nav.screen}{' '}
                    (
                    {new Date(
                      nav.timestamp
                    ).toLocaleTimeString()}
                    )
                  </div>
                )
              )}
            </div>
          </section>
        )}

        {/* ==================================================
            FULL STACK
        ================================================== */}

        {showDebugInfo &&
          errorStack.length >
            0 && (
            <section className="error-detail-section">
              <div className="error-detail-label">
                📚 Full Component Stack
              </div>

              <pre className="error-stack-container">
                {errorStack
                  .map(
                    (
                      item
                    ) =>
                      item.raw
                  )
                  .join('\n')}
              </pre>
            </section>
          )}

        {/* ==================================================
            TOGGLE DEBUG
        ================================================== */}

        <button
          type="button"
          className="debug-toggle"
          onClick={() =>
            this.setState(
              prevState => ({
                showDebugInfo:
                  !prevState.showDebugInfo,
              })
            )
          }
        >
          {showDebugInfo
            ? 'Hide'
            : 'Show'}{' '}
          Full Stack Trace
        </button>
      </div>
    );
  };

  // ==========================================================
  // RENDER
  // ==========================================================

  render() {
    if (
      this.state.hasError
    ) {
      return (
        <div className="error-boundary-container">

          {/* ==================================================
              HEADER
          ================================================== */}

          <header className="error-boundary-header">
            <h1 className="error-boundary-title">
              ⚠️ Something went wrong
            </h1>

            <p className="error-boundary-subtitle">
              We're sorry for the
              inconvenience. The
              application has
              encountered an error.
            </p>
          </header>

          {/* ==================================================
              CONTENT
          ================================================== */}

          <main className="error-boundary-content">

            {this.renderErrorDetails()}

            {/* ================================================
                ACTIONS
            ================================================= */}

            <div className="error-boundary-actions">

              <button
                type="button"
                className="error-button primary"
                onClick={
                  this.handleRetry
                }
              >
                Try Again
              </button>

              <button
                type="button"
                className="error-button secondary"
                onClick={
                  this.handleReset
                }
              >
                Go to Home
              </button>

              <button
                type="button"
                className="error-button tertiary"
                onClick={
                  this.shareErrorLog
                }
              >
                Share Error Report
              </button>
            </div>

            {/* ================================================
                HELP
            ================================================= */}

            <section className="error-help-section">

              <h3 className="error-help-title">
                Need Help?
              </h3>

              <p className="error-help-text">
                Error ID:{' '}
                {this.state.errorId ||
                  'N/A'}
              </p>

              <p className="error-help-text">
                Screen:{' '}
                {this.state.currentScreen ||
                  'Unknown'}
              </p>

              <p className="error-help-text">
                Path:{' '}
                {this.state.currentPath ||
                  window.location.pathname}
              </p>

              <button
                type="button"
                className="error-copy-button"
                onClick={
                  this.copyErrorDetails
                }
              >
                Copy Error Details
              </button>
            </section>

          </main>
        </div>
      );
    }

    return this.props.children;
  }
}

// ============================================================
// REACT ROUTER HOC
// ============================================================
//
// This allows ErrorBoundary to receive:
//
// navigation
// location
// params
//
// without putting React Router hooks inside the
// class-based ErrorBoundary.
//

export const ErrorBoundaryWithRouter = (
  props
) => {
  const location =
    useLocation();

  const navigate =
    useNavigate();

  const params =
    useParams();

  return (
    <ErrorBoundary
      {...props}
      location={location}
      params={params}
      navigation={navigate}
    />
  );
};

// ============================================================
// CSS
// ============================================================

export const errorBoundaryStyles = `
.error-boundary-container {
  min-height: 100vh;
  width: 100%;
  box-sizing: border-box;
  background: #f5f5f5;
  color: #333333;
  display: flex;
  flex-direction: column;
  overflow: auto;
}

.error-boundary-header {
  width: 100%;
  box-sizing: border-box;
  padding: 24px;
  background: #ffffff;
  border-bottom: 1px solid #e0e0e0;
}

.error-boundary-title {
  margin: 0 0 8px 0;
  font-size: 24px;
  line-height: 1.3;
  font-weight: 700;
  color: #333333;
}

.error-boundary-subtitle {
  margin: 0;
  font-size: 16px;
  line-height: 1.5;
  color: #666666;
}

.error-boundary-content {
  width: 100%;
  max-width: 1000px;
  box-sizing: border-box;
  margin: 0 auto;
  padding: 24px;
}

.error-details {
  width: 100%;
  box-sizing: border-box;
  background: #ffffff;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  max-height: 500px;
  overflow-y: auto;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.error-detail-title {
  margin: 0 0 20px 0;
  font-size: 19px;
  font-weight: 600;
  color: #333333;
}

.error-detail-section {
  margin-bottom: 16px;
}

.error-detail-label {
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #666666;
}

.error-detail-value {
  background: #f5f5f5;
  padding: 12px;
  border-radius: 8px;
  overflow-wrap: anywhere;
}

.error-detail-text {
  font-size: 14px;
  line-height: 1.5;
  color: #333333;
}

.error-detail-subtext {
  margin: 5px 0 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: #666666;
  font-family: monospace;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.error-message {
  background: #fff0f0;
  border: 1px solid #ffcccc;
}

.error-stack-line {
  margin-bottom: 4px;
  font-size: 12px;
  line-height: 1.5;
  color: #666666;
  font-family: monospace;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.error-stack-container {
  margin: 0;
  padding: 14px;
  max-height: 300px;
  overflow: auto;
  background: #1e1e1e;
  color: #eeeeee;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.5;
  font-family: monospace;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.debug-toggle {
  display: block;
  margin: 10px auto 0;
  padding: 8px 12px;
  border: none;
  background: transparent;
  color: #007aff;
  font-size: 13px;
  cursor: pointer;
}

.debug-toggle:hover {
  text-decoration: underline;
}

.error-boundary-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
}

.error-button {
  width: 100%;
  min-height: 52px;
  padding: 14px 24px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition:
    opacity 0.2s ease,
    transform 0.1s ease;
  box-sizing: border-box;
}

.error-button:hover {
  opacity: 0.9;
}

.error-button:active {
  transform: scale(0.99);
}

.error-button.primary {
  border: 1px solid #007aff;
  background: #007aff;
  color: #ffffff;
}

.error-button.secondary {
  border: 1px solid #007aff;
  background: #ffffff;
  color: #007aff;
}

.error-button.tertiary {
  border: 1px solid transparent;
  background: transparent;
  color: #666666;
}

.error-help-section {
  box-sizing: border-box;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 20px;
}

.error-help-title {
  margin: 0 0 10px 0;
  font-size: 17px;
  font-weight: 600;
  color: #333333;
}

.error-help-text {
  margin: 0 0 6px 0;
  font-size: 14px;
  line-height: 1.5;
  color: #666666;
  overflow-wrap: anywhere;
}

.error-copy-button {
  margin-top: 10px;
  padding: 8px 0;
  border: none;
  background: transparent;
  color: #007aff;
  font-size: 14px;
  text-decoration: underline;
  cursor: pointer;
}

.error-boundary-notification {
  position: fixed;
  left: 50%;
  bottom: 24px;
  transform: translateX(-50%);
  z-index: 999999;
  max-width: calc(100vw - 40px);
  padding: 12px 18px;
  border-radius: 8px;
  background: #333333;
  color: #ffffff;
  font-size: 14px;
  line-height: 1.4;
  box-shadow:
    0 4px 20px rgba(0, 0, 0, 0.2);
}

@media (max-width: 600px) {
  .error-boundary-header {
    padding: 18px;
  }

  .error-boundary-title {
    font-size: 21px;
  }

  .error-boundary-subtitle {
    font-size: 14px;
  }

  .error-boundary-content {
    padding: 14px;
  }

  .error-details {
    padding: 14px;
    max-height: 55vh;
  }

  .error-button {
    min-height: 48px;
    font-size: 15px;
  }
}
`;

// ============================================================
// DEFAULT EXPORT
// ============================================================

export default ErrorBoundary;
